import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

interface KpiCardProps {
  label: string;
  value: string | number;
  /** Applied to the main value span (e.g. tier color). */
  valueClassName?: string;
  /** Second line under the main value (small, muted). */
  subValue?: string;
  /** Second line typography (defaults to muted). */
  subValueClassName?: string;
  /** Rounded percentile vs other divisions (0–100). Same definition as division summary. */
  percentile?: number;
}

function isDisabledValue(value: string | number): boolean {
  return value === 0 || value === '0' || value === '—';
}

export function KpiCard({
  label,
  value,
  valueClassName,
  subValue,
  subValueClassName,
  percentile,
}: KpiCardProps) {
  const disabled = isDisabledValue(value);
  const tooltipText =
    percentile !== undefined
      ? `Top ${100 - percentile}% — higher than ${percentile}% of divisions`
      : undefined;

  const inner = (
    <div
      className={cn(
        'flex flex-col items-center rounded-lg border bg-muted/30 px-2 py-1.5 flex-1 min-w-[75px]',
        disabled && 'opacity-35',
        percentile !== undefined && 'cursor-help'
      )}
    >
      <span
        className={cn(
          'text-sm font-bold tabular-nums leading-none',
          valueClassName ?? 'text-foreground'
        )}
      >
        {value}
      </span>
      <span className="text-[10px] text-muted-foreground mt-0.5 text-center leading-tight">
        {label}
      </span>
      {subValue !== undefined ? (
        <span
          className={cn(
            'text-[9px] mt-0.5 text-center leading-tight tabular-nums',
            subValueClassName ?? 'text-muted-foreground'
          )}
        >
          {subValue}
        </span>
      ) : null}
      {percentile !== undefined ? (
        <span className="text-[9px] text-muted-foreground mt-0.5 text-center leading-tight tabular-nums">
          Higher than {percentile}% of divisions
        </span>
      ) : null}
    </div>
  );

  if (tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs text-xs">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return inner;
}

interface KpiRowProps {
  children: React.ReactNode;
}

export function KpiRow({ children }: KpiRowProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {children}
    </div>
  );
}

